import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Users, CalendarDays, ClipboardCheck, BarChart3, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, key: 'dashboard' },
  { to: '/team', icon: Users, key: 'team' },
  { to: '/scheduler', icon: CalendarDays, key: 'scheduler' },
  { to: '/attendance', icon: ClipboardCheck, key: 'attendance' },
  { to: '/reports', icon: BarChart3, key: 'reports' },
  { to: '/settings', icon: Settings, key: 'settings' },
];

function getInitials(fullName) {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Sidebar({ isCollapsed, onToggle }) {
  const { t } = useTranslation('nav');
  const auth = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };

  const initials = getInitials(auth.fullName);
  const roleLabel = t(`role.${auth.role}`, { defaultValue: auth.role ?? 'Staff' });

  return (
    <aside
      className={`flex flex-col bg-gray-50 border-r border-gray-200 h-screen sticky top-0 transition-all duration-200 shrink-0 ${
        isCollapsed ? 'w-[60px]' : 'w-[180px]'
      }`}
    >
      {/* Header: wordmark + toggle */}
      <div className="flex items-center h-16 px-3 border-b border-gray-200 gap-2">
        <button
          onClick={onToggle}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 border border-gray-200 shadow-sm"
          title={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          aria-label={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
        >
          <span className="text-sm font-extrabold tracking-tight">
            <span style={{ color: '#085041' }}>B</span>
            <span className="text-orange-600">P</span>
          </span>
        </button>
        {!isCollapsed && (
          <span className="text-base font-bold tracking-tight truncate">
            <span style={{ color: '#085041' }}>Bento</span>
            <span className="text-orange-600">Ping</span>
          </span>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-3 space-y-0.5 px-2 overflow-y-auto">
        {NAV_ITEMS.map(({ to, icon: Icon, key }) => (
          <NavLink
            key={to}
            to={to}
            title={isCollapsed ? t(key) : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-white border border-gray-200 shadow-sm text-orange-600'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              } ${isCollapsed ? 'justify-center' : ''}`
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  size={18}
                  className={`flex-shrink-0 ${isActive ? 'text-orange-500' : 'text-gray-400'}`}
                />
                {!isCollapsed && <span className="truncate">{t(key)}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 py-3 px-2 space-y-1">
        {/* User avatar + info */}
        <div className={`flex items-center gap-2 px-2 py-2 ${isCollapsed ? 'justify-center' : ''}`}>
          <div
            className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-xs font-bold"
            title={isCollapsed ? (auth.fullName ?? auth.email ?? '') : undefined}
          >
            {initials}
          </div>
          {!isCollapsed && (
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-800 truncate">{auth.fullName ?? auth.email}</p>
              <p className="text-[10px] text-gray-400 truncate">{roleLabel}</p>
            </div>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`flex items-center gap-2 w-full px-2 py-2 rounded-lg text-sm text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors ${
            isCollapsed ? 'justify-center' : ''
          }`}
          title={isCollapsed ? t('logout') : undefined}
        >
          <LogOut size={16} className="flex-shrink-0" />
          {!isCollapsed && <span>{t('logout')}</span>}
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;